
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Spinner from '../../components/ui/Spinner';
import MessageBox from '../../components/ui/MessageBox';
import Button from '../../components/ui/Button';
import html2canvas from 'html2canvas';
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle, FileEdit, XCircle, Printer, Send } from "lucide-react";

// --- Print Preview Modal Component ---
const PrintPreview = ({ htmlContent, onClose, order }) => {
  const hiddenRef = React.useRef(null);

  const handlePrint = () => {
    const iframe = document.getElementById('print-iframe');
    if (iframe) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const handleWhatsAppShare = async () => {
    if (!hiddenRef.current) {
      alert('Content not ready.');
      return;
    }

    const countryCode = '91';
    const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, '') || '';
    if (!mobileNumber) {
      alert('Mobile number not available for WhatsApp share.');
      return;
    }
    const fullNumber = countryCode + mobileNumber;

    try {
      const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true, backgroundColor: null });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (blob) {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        alert('Content copied to clipboard!');
      }
    } catch (err) {
      console.warn('Clipboard copy failed:', err);
    }

    const message = encodeURIComponent('Copied invoice/packing slip');
    window.open(`https://wa.me/${fullNumber}?text=${message}`, '_blank');
  };

   

  return (
    <>
      <div
        ref={hiddenRef}
        style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: '800px', backgroundColor: 'white', padding: '10px' }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
        <div className="relative w-full max-w-sm p-4 bg-white rounded-lg shadow-2xl">
          <h3 className="mb-2 text-lg font-semibold text-center">Preview</h3>
          <div className="border border-gray-300">
            <iframe
              id="print-iframe"
              srcDoc={htmlContent}
              title="Print Preview"
              style={{ width: '100%', height: '60vh', border: 'none' }}
            />
          </div>

          <div className="flex justify-end gap-7 mt-2">
            <Button onClick={onClose} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
               bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md 
               hover:from-red-600 hover:to-red-700 hover:shadow-lg 
               transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <XCircle size={16} />
              Close
            </Button>

            <Button onClick={handlePrint} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
               bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
               hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
               transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <Printer size={16} />
              Print
            </Button>

            <Button onClick={handleWhatsAppShare}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
               bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-md 
               hover:from-green-600 hover:to-emerald-700 hover:shadow-lg 
               transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <Send size={16} />
              WhatsApp
            </Button>
          </div>

        </div>
      </div>
    </>
  );
};

const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);
const OrderCard = ({
  order,
  onEdit,
  MakeReceipt,
  onPrintInvoice,
  onPackingSlip,
  receiptDisabled,
  orderStatuses,
  handlePending,
  handleJobto,
}) => (
  <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition">
    {/* --- Header --- */}
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-gray-900">{order.Vname}</h3>
      <p className="text-xs text-gray-500">#{order.Poid}</p>
      <p className="text-xs text-gray-500">
        {order.DeliveryDate
          ? new Date(order.DeliveryDate).toLocaleDateString()
          : ""}
      </p>
    </div>

    {/* --- Invoice & Qty --- */}
    {order.InvNo && (
      <p className="text-sm font-medium text-gray-700">{order.InvNo}</p>
    )}
    <p className="text-sm text-gray-600">
      {order.SumQty} / {order.Rate} &nbsp; | &nbsp; Rs:{order.OrderTotal}
    </p>
    <p className="text-sm text-gray-600 mb-3">
      Job: {order.JobToName || "Jobto"}
    </p>

    {/* --- Actions --- */}
    <div className="grid grid-cols-2 gap-2">
      <Button
        className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-1 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
        onClick={onEdit}
      >
        <FileEdit className="w-5 h-5" />
        Edit
      </Button>

      <Button
        className={`w-full px-3 py-1.5 text-xs font-medium rounded-md ${receiptDisabled
            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
            : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        onClick={MakeReceipt}
        disabled={receiptDisabled}
      >
        {receiptDisabled ? "Receipt Made" : "Make Receipt"}
      </Button>

      <Button
        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
        onClick={onPrintInvoice}
      >
        Invoice
      </Button>

      <Button
        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
        onClick={() => onPackingSlip(order)}
      >
        Packing Slip
      </Button>

      <Button
        key={order.Poid}
        onClick={() => handlePending(order)}
        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600"
      >
        {orderStatuses[order.Poid] || order.Status}
      </Button>

      <Button
        onClick={() => handleJobto(order.Poid)}
        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
      >
        Job: {order.JobToName ? order.JobToName : "Jobto"}
      </Button>
    </div>
  </div>
);


// --- Order List Page Component ---
const OrderListPage = ({ setView, setEditingContext }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [disabledReceipts, setDisabledReceipts] = useState(new Set());
  const [disabledInvoices, setDisabledInvoices] = useState(new Set());
  const [printContent, setPrintContent] = useState('');
  const [currentOrder, setCurrentOrder] = useState(null);
  const [viewMode, setViewMode] = useState("card");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    raised: false,
    pending: false,
    packed: false,
    notPacked: false,
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeFilterType, setActiveFilterType] = useState(null); // "invoice" | "packing"

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        setView("add-order"); // same as clicking Orders menu/button
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // 🔎 Filter orders by search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOrders(orders);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredOrders(
        orders.filter(
          (o) =>
            (o.Vname && o.Vname.toLowerCase().includes(lower)) ||
            (o.MobileNo && o.MobileNo.toLowerCase().includes(lower)) ||
            (o.InvNo && o.InvNo.toLowerCase().includes(lower)) ||
            (o.Poid && String(o.Poid).toLowerCase().includes(lower))
        )
      );
    }
  }, [searchTerm, orders]);

  useEffect(() => {
    localStorage.removeItem('disabledReceipts');
    localStorage.removeItem('disabledInvoices');
    setDisabledReceipts(new Set());
    setDisabledInvoices(new Set());
  }, []);

  const fetchOrders = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      setError("");
      try {
        const data = await api.getSimplePOList(user.UsrName);
        let list = (data?.Table || []).map((o) => ({
          ...o,
          cpid: Number(o.cpid) || 0,
        }));

        list = list.filter(
          (obj, index, self) =>
            index === self.findIndex(
              (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
            )
        );

        setOrders(list);
        setFilteredOrders(list);

        const updates = {};
        list.forEach((o) => {
          updates[o.Poid] = o.Status || "Pending";
        });
        setOrderStatuses(updates);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to fetch orders.");
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [user.UsrName]
  );

  useEffect(() => {
    let mounted = true;
    const loadInitialOrders = async () => {
      setLoading(true);
      try {
        await fetchOrders(false);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }

    };
    loadInitialOrders();
    return () => {
      mounted = false;
    };
  }, [fetchOrders]);

  const handleMakeReceipt = async (order) => {
    if (disabledReceipts.has(order.Poid)) return;
    const updated = new Set(disabledReceipts).add(order.Poid);
    setDisabledReceipts(updated);
    localStorage.setItem('disabledReceipts', JSON.stringify([...updated]));
    try { await api.saveMakeReceipt({ Poid: order.Poid }); alert('Receipt successfully generated!'); }
    catch (err) { console.error(err); alert('Failed to generate receipt.'); }
  };

  const handleMakeInvoice = async (order) => {
    if (disabledInvoices.has(order.Poid) || Number(order.cpid) > 0) return;
    const updated = new Set(disabledInvoices).add(order.Poid);
    setDisabledInvoices(updated);
    localStorage.setItem('disabledInvoices', JSON.stringify([...updated]));
    try {
      const resp = await api.MobGetfinalizeOrder(order.Poid);
      const maybeCpid = resp?.Table4?.[0]?.Cpid ?? resp?.data?.Table4?.[0]?.Cpid ?? resp?.MobGetfinalizeOrderjs?.Table4?.[0]?.Cpid ?? null;
      const newCpid = Number(maybeCpid) || 0;
      if (newCpid > 0) {
        const updateCpid = (o) => (o.Poid === order.Poid ? { ...o, cpid: newCpid } : o);
        setOrders((prev) => prev.map(updateCpid));
        setFilteredOrders((prev) => prev.map(updateCpid));
        await fetchOrders(false);
        alert('Invoice successfully generated!');
      } else alert('Invoice API returned no Cpid.');
    } catch (err) { console.error(err); alert('Failed to generate invoice.'); }
  };

  const handlePrintInvoice = async (order) => {
    try {
      const resp = await api.MobGetfinalizeOrder(order.Poid);
      const content = resp?.Table?.[0]?.PrintContent ?? resp?.Table3?.[0]?.PrintContent ?? resp?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ?? resp?.data?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ?? null;
      if (content) {
        setPrintContent(content); setCurrentOrder(order);
        const match = content.match(/Bill No -\s*([^*<]+)/i);
        const newInvNo = match ? match[1].trim() : null;
        if (newInvNo) {
          setOrders((prev) => prev.map((o) => (o.Poid === order.Poid ? { ...o, InvNo: newInvNo } : o)));
          setFilteredOrders((prev) => prev.map((o) => (o.Poid === order.Poid ? { ...o, InvNo: newInvNo } : o)));
        }
      } else alert('Print content not found.');
    } catch (err) { console.error(err); alert('Error fetching print content.'); }
  };




  // --- inside OrderListPage ---
  const [orderStatuses, setOrderStatuses] = useState({});

  // ✅ Called only when Pending button clicked
  const handlePending = async (order) => {
    console.log("handlePending called with:", order);

    try {
      const response = await api.GetPoOrderItemlistAPI(order.Poid, user.UsrName);
      const statusData = response?.Table?.[0] ?? null;

      if (statusData) {
        // save context
        localStorage.setItem("poidContent", order.Poid);
        localStorage.setItem("username", user.UsrName);

        // navigate to pending view
        setView("pending-view");

        // update this order's status in UI
        setOrderStatuses((prev) => ({
          ...prev,
          [order.Poid]: statusData.Status,
        }));
      } else {
        alert("No status data found.");
      }
    } catch (err) {
      console.error("Error fetching order item list:", err);
      alert("Failed to fetch order items.");
    }
  };

  const handleJobto = async (poid) => {
    try {
      localStorage.setItem("poidContent", poid);   // ✅ Save clicked Poid

      const resp = await api.GetRmpUserAPI(user.UsrName);
      console.log("GetRmpUserAPI resp:", resp);

      setView("AllotOrderToPerson"); // ✅ Now allot view will read correct Poid
    } catch (err) {
      console.error(err);
      alert("Error fetching job allocation data.");
    }
  };


  const handlePackingSlip = async (order) => {
    try {
      const resp = await api.get(`GetHtmlPackingSlipAPI?PoId=${order.Poid}`);
      console.log('Packing Slip API Response:', resp);

      // Access Table directly on resp, not resp.data
      const content = resp?.Table?.[0]?.PrintPackingSlip ?? null;

      if (content) {
        setPrintContent(content);
        setCurrentOrder(order);
      } else {
        alert('Packing slip content not found.');
        console.warn('Packing slip content could not be found in API response:', resp);
      }
    } catch (err) {
      console.error('Error fetching packing slip:', err);
      alert('Failed to fetch packing slip.');
    }
  };



  return (
    <div className="p-4">
      {printContent && currentOrder && (
        <PrintPreview
          htmlContent={printContent}
          onClose={() => {
            setPrintContent("");
            setCurrentOrder(null);
          }}
          order={currentOrder}
        />
      )}
      {isFilterOpen && activeFilterType === "packing" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80">
            <h3 className="text-lg font-semibold mb-4">Filter by Packing</h3>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterOptions.packed}
                  onChange={(e) =>
                    setFilterOptions((prev) => ({ ...prev, packed: e.target.checked }))
                  }
                />
                Packed
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterOptions.notPacked}
                  onChange={(e) =>
                    setFilterOptions((prev) => ({ ...prev, notPacked: e.target.checked }))
                  }
                />
                Pending
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button onClick={() => setIsFilterOpen(false)} className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400">
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  setIsFilterOpen(false);
                  try {
                    const data = await api.getSimplePOList(user.UsrName);
                    let list = (data?.Table || []).map((o) => ({ ...o, cpid: Number(o.cpid) || 0 }));

                    // Deduplicate
                    list = list.filter(
                      (obj, index, self) =>
                        index === self.findIndex((t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo)
                    );

                    if (filterOptions.packed && !filterOptions.notPacked) {
                      list = list.filter((o) => o.Status === "Packed");
                    } else if (!filterOptions.packed && filterOptions.notPacked) {
                      list = list.filter((o) => o.Status === "Pending");
                    }

                    setFilterOptions((prev) => ({ ...prev, raised: false, pending: false }));
                    setOrders(list);
                    setFilteredOrders(list);
                  } catch (err) {
                    console.error("Packing filter error:", err);
                    alert("Failed to filter orders.");
                  }
                }}
                className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {isFilterOpen && activeFilterType === "invoice" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80">
            <h3 className="text-lg font-semibold mb-4">Filter by Invoice</h3>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterOptions.raised}
                  onChange={(e) =>
                    setFilterOptions((prev) => ({ ...prev, raised: e.target.checked }))
                  }
                />
                Raised
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterOptions.pending}
                  onChange={(e) =>
                    setFilterOptions((prev) => ({
                      ...prev,
                      pending: e.target.checked,
                    }))
                  }
                />
                Pending
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button
                onClick={() => setIsFilterOpen(false)}
                className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  setIsFilterOpen(false);
                  try {
                    const data = await api.getSimplePOList(user.UsrName);
                    let list = (data?.Table || []).map((o) => ({
                      ...o,
                      cpid: Number(o.cpid) || 0,
                    }));

                    list = list.filter(
                      (obj, index, self) =>
                        index ===
                        self.findIndex(
                          (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
                        )
                    );

                    // ✅ Apply invoice filters
                    if (filterOptions.raised && !filterOptions.pending) {
                      list = list.filter((o) => o.InvNo && o.InvNo !== "InvNo :");
                    } else if (!filterOptions.raised && filterOptions.pending) {
                      list = list.filter((o) => !o.InvNo || o.InvNo === "InvNo :");
                    }

                    setFilterOptions((prev) => ({ ...prev, packed: false, notPacked: false }));

                    setOrders(list);
                    setFilteredOrders(list);
                  } catch (err) {
                    console.error("Invoice filter error:", err);
                    alert("Failed to filter orders.");
                  }
                }}
                className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {
        isFilterOpen && activeFilterType === "jobto" && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-80">
              <h3 className="text-lg font-semibold mb-4">Filter by JobTo</h3>

              <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                {/* ✅ Get distinct JobToName values */}
                {[...new Set(orders.map((o) => o.JobToName).filter(Boolean))].map(
                  (job) => (
                    <label key={job} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={filterOptions[job] || false}
                        onChange={(e) =>
                          setFilterOptions((prev) => ({
                            ...prev,
                            [job]: e.target.checked,
                          }))
                        }
                      />
                      {job}
                    </label>
                  )
                )}
              </div>

              {isFilterOpen && activeFilterType === "jobto" && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="bg-white p-6 rounded-lg shadow-lg w-80">
                    <h3 className="text-lg font-semibold mb-4">Filter by JobTo</h3>

                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                      {Object.values(
                        orders.reduce((acc, o) => {
                          const raw = o.JobToName?.trim() || "";
                          const display = raw === "" ? "(Blank)" : o.JobToName.trim();
                          const key = display.toLowerCase();

                          if (!acc[key]) acc[key] = display;
                          return acc;
                        }, {})
                      ).map((job) => (
                        <label key={job.toLowerCase()} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={filterOptions[job.toLowerCase()] || false}
                            onChange={(e) =>
                              setFilterOptions((prev) => ({
                                ...prev,
                                [job.toLowerCase()]: e.target.checked,
                              }))
                            }
                          />
                          {job}
                        </label>
                      ))}
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                      <Button
                        onClick={() => setIsFilterOpen(false)}
                        className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          setIsFilterOpen(false);
                          try {
                            const data = await api.getSimplePOList(user.UsrName);
                            let list = (data?.Table || []).map((o) => ({
                              ...o,
                              cpid: Number(o.cpid) || 0,
                            }));

                            // Unique orders
                            list = list.filter(
                              (obj, index, self) =>
                                index ===
                                self.findIndex(
                                  (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
                                )
                            );

                            // Apply JobTo filter
                            const selectedJobs = Object.keys(filterOptions).filter(
                              (k) => filterOptions[k]
                            );
                            if (selectedJobs.length > 0) {
                              list = list.filter((o) => {
                                const normalized = o.JobToName?.trim() || "";
                                const jobKey =
                                  normalized === "" ? "(blank)" : normalized.toLowerCase();
                                return selectedJobs.includes(jobKey);
                              });
                            }

                            setOrders(list);
                            setFilteredOrders(list);
                          } catch (err) {
                            console.error("JobTo filter error:", err);
                            alert("Failed to filter orders.");
                          }
                        }}
                        className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
                      >
                        OK
                      </Button>
                    </div>
                  </div>
                </div>
              )}


              {/* ✅ Buttons INSIDE modal */}
              <div className="flex justify-end gap-3 mt-4">
                <Button
                  onClick={() => setIsFilterOpen(false)}
                  className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    setIsFilterOpen(false);
                    try {
                      const data = await api.getSimplePOList(user.UsrName);
                      let list = (data?.Table || []).map((o) => ({
                        ...o,
                        cpid: Number(o.cpid) || 0,
                      }));

                      list = list.filter(
                        (obj, index, self) =>
                          index ===
                          self.findIndex(
                            (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
                          )
                      );

                      // Apply JobTo filter (case-insensitive)
                      const selectedJobs = Object.keys(filterOptions).filter(
                        (k) => filterOptions[k]
                      );
                      if (selectedJobs.length > 0) {
                        list = list.filter((o) =>
                          selectedJobs.includes(o.JobToName?.toLowerCase().trim())
                        );
                      }

                      setOrders(list);
                      setFilteredOrders(list);
                    } catch (err) {
                      console.error("JobTo filter error:", err);
                      alert("Failed to filter orders.");
                    }
                  }}
                  className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        )}


      <Heading>Orders</Heading>

      <div className="flex flex-col items-center gap-4 mb-6 md:flex-row md:justify-between">

        <Button
          className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
               rounded-2xl shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
               transition-all duration-300 ease-in-out"
          onClick={() => setView("add-order")}  >
          <PlusCircle size={18} className="animate-pulse" />
          <span className="font-medium">New</span>
        </Button>


        {/* 🔄 Toggle buttons */}
        <div className="flex items-center justify-center p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => setViewMode("card")}
            className={`p-2 rounded-md ${viewMode === "card"
              ? "bg-white text-blue-600 shadow"
              : "text-gray-500"
              }`}
          >
            <GridIcon />
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`p-2 rounded-md ${viewMode === "table"
              ? "bg-white text-blue-600 shadow"
              : "text-gray-500"
              }`}
          >
            <ListIcon />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray border-b">
        <Button
          onClick={() => setView("home")}
          className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
        >
          ⬅
        </Button>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by vendor or mobile..."
        />
        <div className="relative">
          {/* Dropdown Trigger */}
          <Button
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className="flex items-center justify-center w-9 h-9 text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition"
          >
            <span className="text-lg">🔽</span> {/* cleaner icon than 🔽 */}
          </Button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div
              className={`absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 transition-all duration-200 ease-out ${isDropdownOpen
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-95 pointer-events-none"
                }`}
            >
              <button
                className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-xl"
                onClick={() => {
                  setActiveFilterType("invoice");
                  setIsDropdownOpen(false);
                  setIsFilterOpen(true);
                }}
              >
                <span className="text-base">📑</span>
                <span>Filter by Invoice</span>
              </button>

              <button
                className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setActiveFilterType("packing");
                  setIsDropdownOpen(false);
                  setIsFilterOpen(true);
                }}
              >
                <span className="text-base">📦</span>
                <span>Filter by Packing</span>
              </button>

              <button
                className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-xl"
                onClick={() => {
                  setActiveFilterType("jobto");
                  setIsDropdownOpen(false);
                  setIsFilterOpen(true);
                }}
              >
                <span className="text-base">🛠</span>
                <span>Filter by JobTo</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* List / Table Content */}
      {
        loading ? (
          <Spinner />
        ) : error ? (
          <MessageBox message={error} />
        ) : filteredOrders.length === 0 ? (
          <div className="py-12 text-center text-gray-500">No orders found.</div>
        ) : viewMode === "card" ? (
          // ✅ Card View
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.Poid}
                order={order}
                onEdit={() => {
                  setEditingContext({
                    id: order.Poid,
                    customerId: order.Vid,
                    orderDate: order.Podate,
                  });
                  setView("edit-order");
                }}
                MakeReceipt={() => handleMakeReceipt(order)}
                MakeInvoice={() => handleMakeInvoice(order)}
                onPrintInvoice={() => handlePrintInvoice(order)}
                onPackingSlip={() => handlePackingSlip(order)}
                receiptDisabled={disabledReceipts.has(order.Poid)}
                invoiceDisabled={
                  disabledInvoices.has(order.Poid) || Number(order.cpid) > 0
                }
                orderStatuses={orderStatuses}        // ✅ now available in OrderCard
                handlePending={handlePending}        // ✅ passed down
                handleJobto={handleJobto}            // ✅ passed down
              />
            ))}
          </div>
        ) : (
          // ✅ Table View
          <div className="overflow-x-auto bg-white border border-gray-200 shadow-md rounded-xl mt-4">
            <table className="w-full text-sm text-left">
              <thead className="tracking-wider text-gray-900 uppercase bg-gray-100">
                <tr>
                  <th className="p-3">PO ID</th>
                  <th className="p-3">Vendor</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Invoice</th>
                  <th className="p-3">Total</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.Poid} className="hover:bg-gray-50">
                    <td className="p-3">#{order.Poid}</td>
                    <td className="p-3">{order.Vname}</td>
                    <td className="p-3">
                      {order.DeliveryDate
                        ? new Date(order.DeliveryDate).toLocaleDateString()
                        : ""}
                    </td>
                    <td className="p-3">{order.InvNo || "-"}</td>
                    <td className="p-3">{order.OrderTotal}</td>
                    <td className="p-3 text-center">
                      <div className="flex justify-center gap-2 flex-wrap">
                        <Button
                          className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-1 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
                          onClick={() => {
                            setEditingContext({
                              id: order.Poid,
                              customerId: order.Vid,
                              orderDate: order.Podate,
                            });
                            setView("edit-order");
                          }}
                        >
                          <FileEdit className="w-6 h-6" />
                          Edit
                        </Button>

                        <Button
                          className={`min-w-[70px] px-2 py-1 rounded ${disabledReceipts.has(order.Poid)
                            ? "bg-gray-300 text-gray-600"
                            : "bg-blue-500 text-white hover:bg-blue-600"
                            }`}
                          onClick={() => handleMakeReceipt(order)}
                          disabled={disabledReceipts.has(order.Poid)}
                        >
                          Receipt
                        </Button>

                        <Button
                          className="min-w-[70px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
                          onClick={() => handlePrintInvoice(order)}
                        >
                          Invoice
                        </Button>

                        <Button
                          className="min-w-[70px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
                          onClick={() => handlePackingSlip(order)}
                        >
                          Packing
                        </Button>
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div >
  );
};

export default OrderListPage;





// import React, { useEffect, useState } from 'react';
// import { useAuth } from '../../context/AuthContext';
// import api from '../../api/api';
// import Spinner from '../../components/ui/Spinner';
// import MessageBox from '../../components/ui/MessageBox';
// import Button from '../../components/ui/Button';
// import html2canvas from 'html2canvas';
// import Heading from "../../components/ui/Heading";
// import SearchInput from "../../components/ui/SearchInput";
// import { PlusCircle, FileEdit, XCircle, Printer, Send } from "lucide-react";

// // --- Print Preview Modal Component ---
// const PrintPreview = ({ htmlContent, onClose, order }) => {
//   const hiddenRef = React.useRef(null);

//   const handlePrint = () => {
//     const iframe = document.getElementById('print-iframe');
//     if (iframe) {
//       iframe.contentWindow.focus();
//       iframe.contentWindow.print();
//     }
//   };

//   const handleWhatsAppShare = async () => {
//     if (!hiddenRef.current) {
//       alert('Content not ready.');
//       return;
//     }

//     const countryCode = '91';
//     const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, '') || '';
//     if (!mobileNumber) {
//       alert('Mobile number not available for WhatsApp share.');
//       return;
//     }
//     const fullNumber = countryCode + mobileNumber;

//     try {
//       const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true, backgroundColor: null });
//       const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
//       if (blob) {
//         await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
//         alert('Content copied to clipboard!');
//       }
//     } catch (err) {
//       console.warn('Clipboard copy failed:', err);
//     }

//     const message = encodeURIComponent('Copied invoice/packing slip');
//     window.open(`https://wa.me/${fullNumber}?text=${message}`, '_blank');
//   };

//   return (
//     <>
//       <div
//         ref={hiddenRef}
//         style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: '800px', backgroundColor: 'white', padding: '10px' }}
//         dangerouslySetInnerHTML={{ __html: htmlContent }}
//       />

//       <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
//         <div className="relative w-full max-w-sm p-4 bg-white rounded-lg shadow-2xl">
//           <h3 className="mb-2 text-lg font-semibold text-center">Preview</h3>
//           <div className="border border-gray-300">
//             <iframe
//               id="print-iframe"
//               srcDoc={htmlContent}
//               title="Print Preview"
//               style={{ width: '100%', height: '60vh', border: 'none' }}
//             />
//           </div>

//           <div className="flex justify-end gap-7 mt-2">
//             <Button onClick={onClose} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
//                bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md 
//                hover:from-red-600 hover:to-red-700 hover:shadow-lg 
//                transition-all duration-300 ease-in-out transform hover:scale-105"
//             >
//               <XCircle size={16} />
//               Close
//             </Button>

//             <Button onClick={handlePrint} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
//                bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
//                hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//                transition-all duration-300 ease-in-out transform hover:scale-105"
//             >
//               <Printer size={16} />
//               Print
//             </Button>

//             <Button onClick={handleWhatsAppShare}
//               className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
//                bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-md 
//                hover:from-green-600 hover:to-emerald-700 hover:shadow-lg 
//                transition-all duration-300 ease-in-out transform hover:scale-105"
//             >
//               <Send size={16} />
//               WhatsApp
//             </Button>
//           </div>

//         </div>
//       </div>
//     </>
//   );
// };

// const GridIcon = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
//     <rect x="3" y="3" width="7" height="7"></rect>
//     <rect x="14" y="3" width="7" height="7"></rect>
//     <rect x="14" y="14" width="7" height="7"></rect>
//     <rect x="3" y="14" width="7" height="7"></rect>
//   </svg>
// );

// const ListIcon = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
//     <line x1="8" y1="6" x2="21" y2="6"></line>
//     <line x1="8" y1="12" x2="21" y2="12"></line>
//     <line x1="8" y1="18" x2="21" y2="18"></line>
//     <line x1="3" y1="6" x2="3.01" y2="6"></line>
//     <line x1="3" y1="12" x2="3.01" y2="12"></line>
//     <line x1="3" y1="18" x2="3.01" y2="18"></line>
//   </svg>
// );
// const OrderCard = ({
//   order,
//   onEdit,
//   MakeReceipt,
//   onPrintInvoice,
//   onPackingSlip,
//   receiptDisabled,
//   orderStatuses,
//   handlePending,
//   handleJobto,
// }) => (
//   <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition">
//     {/* --- Header --- */}
//     <div className="mb-3">
//       <h3 className="text-sm font-semibold text-gray-900">{order.Vname}</h3>
//       <p className="text-xs text-gray-500">#{order.Poid}</p>
//       <p className="text-xs text-gray-500">
//         {order.DeliveryDate
//           ? new Date(order.DeliveryDate).toLocaleDateString()
//           : ""}
//       </p>
//     </div>

//     {/* --- Invoice & Qty --- */}
//     {order.InvNo && (
//       <p className="text-sm font-medium text-gray-700">{order.InvNo}</p>
//     )}
//     <p className="text-sm text-gray-600">
//       {order.SumQty} / {order.Rate} &nbsp; | &nbsp; Rs:{order.OrderTotal}
//     </p>
//     <p className="text-sm text-gray-600 mb-3">
//       Job: {order.JobToName || "Jobto"}
//     </p>

//     {/* --- Actions --- */}
//     <div className="grid grid-cols-2 gap-2">
//       <Button
//         className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-1 text-xs font-medium text-white 
//              bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
//              hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//              transition-all duration-300 ease-in-out"
//         onClick={onEdit}
//       >
//         <FileEdit className="w-5 h-5" />
//         Edit
//       </Button>

//       <Button
//         className={`w-full px-3 py-1.5 text-xs font-medium rounded-md ${receiptDisabled
//             ? "bg-gray-300 text-gray-600 cursor-not-allowed"
//             : "bg-blue-500 text-white hover:bg-blue-600"
//           }`}
//         onClick={MakeReceipt}
//         disabled={receiptDisabled}
//       >
//         {receiptDisabled ? "Receipt Made" : "Make Receipt"}
//       </Button>

//       <Button
//         className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
//         onClick={onPrintInvoice}
//       >
//         Invoice
//       </Button>

//       <Button
//         className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
//         onClick={() => onPackingSlip(order)}
//       >
//         Packing Slip
//       </Button>

//       <Button
//         key={order.Poid}
//         onClick={() => handlePending(order)}
//         className="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600"
//       >
//         {orderStatuses[order.Poid] || order.Status}
//       </Button>

//       <Button
//         onClick={() => handleJobto(order.Poid)}
//         className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-md hover:bg-green-600"
//       >
//         Job: {order.JobToName ? order.JobToName : "Jobto"}
//       </Button>
//     </div>
//   </div>
// );


// // --- Order List Page Component ---
// const OrderListPage = ({ setView, setEditingContext }) => {
//   const { user } = useAuth();
//   const [orders, setOrders] = useState([]);
//   const [filteredOrders, setFilteredOrders] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [disabledReceipts, setDisabledReceipts] = useState(new Set());
//   const [disabledInvoices, setDisabledInvoices] = useState(new Set());
//   const [printContent, setPrintContent] = useState('');
//   const [currentOrder, setCurrentOrder] = useState(null);
//   const [viewMode, setViewMode] = useState("card");
//   const [isFilterOpen, setIsFilterOpen] = useState(false);
//   const [filterOptions, setFilterOptions] = useState({
//     raised: false,
//     pending: false,
//     packed: false,
//     notPacked: false,
//   });

//   const [isDropdownOpen, setIsDropdownOpen] = useState(false);
//   const [activeFilterType, setActiveFilterType] = useState(null); // "invoice" | "packing"

//   // 🔎 Filter orders by search term
//   useEffect(() => {
//     if (!searchTerm) {
//       setFilteredOrders(orders);
//     } else {
//       const lower = searchTerm.toLowerCase();
//       setFilteredOrders(
//         orders.filter(
//           (o) =>
//             (o.Vname && o.Vname.toLowerCase().includes(lower)) ||
//             (o.MobileNo && o.MobileNo.toLowerCase().includes(lower)) ||
//             (o.InvNo && o.InvNo.toLowerCase().includes(lower)) ||
//             (o.Poid && String(o.Poid).toLowerCase().includes(lower))
//         )
//       );
//     }
//   }, [searchTerm, orders]);

//   useEffect(() => {
//     localStorage.removeItem('disabledReceipts');
//     localStorage.removeItem('disabledInvoices');
//     setDisabledReceipts(new Set());
//     setDisabledInvoices(new Set());
//   }, []);

//   useEffect(() => {
//     let mounted = true;
//     const fetchOrders = async () => {
//       setLoading(true);
//       try {
//         const data = await api.getSimplePOList(user.UsrName);
//         let list = (data?.Table || []).map((o) => ({
//           ...o,
//           cpid: Number(o.cpid) || 0,
//         }));

//         list = list.filter(
//           (obj, index, self) =>
//             index === self.findIndex(
//               (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
//             )
//         );

//         setOrders(list);
//         setFilteredOrders(list);

//         const updates = {};
//         list.forEach((o) => {
//           updates[o.Poid] = o.Status || "Pending";
//         });
//         setOrderStatuses(updates);
//       } catch (err) {
//         console.error("Fetch error:", err);
//         if (!mounted) return;
//         setError("Failed to fetch orders.");
//       } finally {
//         if (!mounted) return;
//         setLoading(false);
//       }

//     };
//     fetchOrders();
//     return () => {
//       mounted = false;
//     };
//   }, [user.UsrName]);

//   const handleMakeReceipt = async (order) => {
//     if (disabledReceipts.has(order.Poid)) return;
//     const updated = new Set(disabledReceipts).add(order.Poid);
//     setDisabledReceipts(updated);
//     localStorage.setItem('disabledReceipts', JSON.stringify([...updated]));
//     try { await api.saveMakeReceipt({ Poid: order.Poid }); alert('Receipt successfully generated!'); }
//     catch (err) { console.error(err); alert('Failed to generate receipt.'); }
//   };

//   const handleMakeInvoice = async (order) => {
//     if (disabledInvoices.has(order.Poid) || Number(order.cpid) > 0) return;
//     const updated = new Set(disabledInvoices).add(order.Poid);
//     setDisabledInvoices(updated);
//     localStorage.setItem('disabledInvoices', JSON.stringify([...updated]));
//     try {
//       const resp = await api.MobGetfinalizeOrder(order.Poid);
//       const maybeCpid = resp?.Table4?.[0]?.Cpid ?? resp?.data?.Table4?.[0]?.Cpid ?? resp?.MobGetfinalizeOrderjs?.Table4?.[0]?.Cpid ?? null;
//       const newCpid = Number(maybeCpid) || 0;
//       if (newCpid > 0) {
//         const updateCpid = (o) => (o.Poid === order.Poid ? { ...o, cpid: newCpid } : o);
//         setOrders((prev) => prev.map(updateCpid));
//         setFilteredOrders((prev) => prev.map(updateCpid));
//         alert('Invoice successfully generated!');
//       } else alert('Invoice API returned no Cpid.');
//     } catch (err) { console.error(err); alert('Failed to generate invoice.'); }
//   };

//   const handlePrintInvoice = async (order) => {
//     try {
//       const resp = await api.MobGetfinalizeOrder(order.Poid);
//       const content = resp?.Table?.[0]?.PrintContent ?? resp?.Table3?.[0]?.PrintContent ?? resp?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ?? resp?.data?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ?? null;
//       if (content) {
//         setPrintContent(content); setCurrentOrder(order);
//         const match = content.match(/Bill No -\s*([^*<]+)/i);
//         const newInvNo = match ? match[1].trim() : null;
//         if (newInvNo) {
//           setOrders((prev) => prev.map((o) => (o.Poid === order.Poid ? { ...o, InvNo: newInvNo } : o)));
//           setFilteredOrders((prev) => prev.map((o) => (o.Poid === order.Poid ? { ...o, InvNo: newInvNo } : o)));
//         }
//       } else alert('Print content not found.');
//     } catch (err) { console.error(err); alert('Error fetching print content.'); }
//   };




//   // --- inside OrderListPage ---
//   const [orderStatuses, setOrderStatuses] = useState({});

//   // ✅ Called only when Pending button clicked
//   const handlePending = async (order) => {
//     console.log("handlePending called with:", order);

//     try {
//       const response = await api.GetPoOrderItemlistAPI(order.Poid, user.UsrName);
//       const statusData = response?.Table?.[0] ?? null;

//       if (statusData) {
//         // save context
//         localStorage.setItem("poidContent", order.Poid);
//         localStorage.setItem("username", user.UsrName);

//         // navigate to pending view
//         setView("pending-view");

//         // update this order's status in UI
//         setOrderStatuses((prev) => ({
//           ...prev,
//           [order.Poid]: statusData.Status,
//         }));
//       } else {
//         alert("No status data found.");
//       }
//     } catch (err) {
//       console.error("Error fetching order item list:", err);
//       alert("Failed to fetch order items.");
//     }
//   };

//   const handleJobto = async (poid) => {
//     try {
//       localStorage.setItem("poidContent", poid);   // ✅ Save clicked Poid

//       const resp = await api.GetRmpUserAPI(user.UsrName);
//       console.log("GetRmpUserAPI resp:", resp);

//       setView("AllotOrderToPerson"); // ✅ Now allot view will read correct Poid
//     } catch (err) {
//       console.error(err);
//       alert("Error fetching job allocation data.");
//     }
//   };


//   const handlePackingSlip = async (order) => {
//     try {
//       const resp = await api.get(`GetHtmlPackingSlipAPI?PoId=${order.Poid}`);
//       console.log('Packing Slip API Response:', resp);

//       // Access Table directly on resp, not resp.data
//       const content = resp?.Table?.[0]?.PrintPackingSlip ?? null;

//       if (content) {
//         setPrintContent(content);
//         setCurrentOrder(order);
//       } else {
//         alert('Packing slip content not found.');
//         console.warn('Packing slip content could not be found in API response:', resp);
//       }
//     } catch (err) {
//       console.error('Error fetching packing slip:', err);
//       alert('Failed to fetch packing slip.');
//     }
//   };



//   return (
//     <div className="p-4">
//       {printContent && currentOrder && (
//         <PrintPreview
//           htmlContent={printContent}
//           onClose={() => {
//             setPrintContent("");
//             setCurrentOrder(null);
//           }}
//           order={currentOrder}
//         />
//       )}
//       {isFilterOpen && activeFilterType === "packing" && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-80">
//             <h3 className="text-lg font-semibold mb-4">Filter by Packing</h3>

//             <div className="flex flex-col gap-2">
//               <label className="flex items-center gap-2">
//                 <input
//                   type="checkbox"
//                   checked={filterOptions.packed}
//                   onChange={(e) =>
//                     setFilterOptions((prev) => ({ ...prev, packed: e.target.checked }))
//                   }
//                 />
//                 Packed
//               </label>

//               <label className="flex items-center gap-2">
//                 <input
//                   type="checkbox"
//                   checked={filterOptions.notPacked}
//                   onChange={(e) =>
//                     setFilterOptions((prev) => ({ ...prev, notPacked: e.target.checked }))
//                   }
//                 />
//                 Pending
//               </label>
//             </div>

//             <div className="flex justify-end gap-3 mt-4">
//               <Button onClick={() => setIsFilterOpen(false)} className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400">
//                 Cancel
//               </Button>
//               <Button
//                 onClick={async () => {
//                   setIsFilterOpen(false);
//                   try {
//                     const data = await api.getSimplePOList(user.UsrName);
//                     let list = (data?.Table || []).map((o) => ({ ...o, cpid: Number(o.cpid) || 0 }));

//                     // Deduplicate
//                     list = list.filter(
//                       (obj, index, self) =>
//                         index === self.findIndex((t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo)
//                     );

//                     if (filterOptions.packed && !filterOptions.notPacked) {
//                       list = list.filter((o) => o.Status === "Packed");
//                     } else if (!filterOptions.packed && filterOptions.notPacked) {
//                       list = list.filter((o) => o.Status === "Pending");
//                     }

//                     setFilterOptions((prev) => ({ ...prev, raised: false, pending: false }));
//                     setOrders(list);
//                     setFilteredOrders(list);
//                   } catch (err) {
//                     console.error("Packing filter error:", err);
//                     alert("Failed to filter orders.");
//                   }
//                 }}
//                 className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
//               >
//                 OK
//               </Button>
//             </div>
//           </div>
//         </div>
//       )}

//       {isFilterOpen && activeFilterType === "invoice" && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-80">
//             <h3 className="text-lg font-semibold mb-4">Filter by Invoice</h3>

//             <div className="flex flex-col gap-2">
//               <label className="flex items-center gap-2">
//                 <input
//                   type="checkbox"
//                   checked={filterOptions.raised}
//                   onChange={(e) =>
//                     setFilterOptions((prev) => ({ ...prev, raised: e.target.checked }))
//                   }
//                 />
//                 Raised
//               </label>

//               <label className="flex items-center gap-2">
//                 <input
//                   type="checkbox"
//                   checked={filterOptions.pending}
//                   onChange={(e) =>
//                     setFilterOptions((prev) => ({
//                       ...prev,
//                       pending: e.target.checked,
//                     }))
//                   }
//                 />
//                 Pending
//               </label>
//             </div>

//             <div className="flex justify-end gap-3 mt-4">
//               <Button
//                 onClick={() => setIsFilterOpen(false)}
//                 className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
//               >
//                 Cancel
//               </Button>
//               <Button
//                 onClick={async () => {
//                   setIsFilterOpen(false);
//                   try {
//                     const data = await api.getSimplePOList(user.UsrName);
//                     let list = (data?.Table || []).map((o) => ({
//                       ...o,
//                       cpid: Number(o.cpid) || 0,
//                     }));

//                     list = list.filter(
//                       (obj, index, self) =>
//                         index ===
//                         self.findIndex(
//                           (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
//                         )
//                     );

//                     // ✅ Apply invoice filters
//                     if (filterOptions.raised && !filterOptions.pending) {
//                       list = list.filter((o) => o.InvNo && o.InvNo !== "InvNo :");
//                     } else if (!filterOptions.raised && filterOptions.pending) {
//                       list = list.filter((o) => !o.InvNo || o.InvNo === "InvNo :");
//                     }

//                     setFilterOptions((prev) => ({ ...prev, packed: false, notPacked: false }));

//                     setOrders(list);
//                     setFilteredOrders(list);
//                   } catch (err) {
//                     console.error("Invoice filter error:", err);
//                     alert("Failed to filter orders.");
//                   }
//                 }}
//                 className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
//               >
//                 OK
//               </Button>
//             </div>
//           </div>
//         </div>
//       )}

//       {
//         isFilterOpen && activeFilterType === "jobto" && (
//           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
//             <div className="bg-white p-6 rounded-lg shadow-lg w-80">
//               <h3 className="text-lg font-semibold mb-4">Filter by JobTo</h3>

//               <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
//                 {/* ✅ Get distinct JobToName values */}
//                 {[...new Set(orders.map((o) => o.JobToName).filter(Boolean))].map(
//                   (job) => (
//                     <label key={job} className="flex items-center gap-2">
//                       <input
//                         type="checkbox"
//                         checked={filterOptions[job] || false}
//                         onChange={(e) =>
//                           setFilterOptions((prev) => ({
//                             ...prev,
//                             [job]: e.target.checked,
//                           }))
//                         }
//                       />
//                       {job}
//                     </label>
//                   )
//                 )}
//               </div>

//               {isFilterOpen && activeFilterType === "jobto" && (
//                 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
//                   <div className="bg-white p-6 rounded-lg shadow-lg w-80">
//                     <h3 className="text-lg font-semibold mb-4">Filter by JobTo</h3>

//                     <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
//                       {Object.values(
//                         orders.reduce((acc, o) => {
//                           const raw = o.JobToName?.trim() || "";
//                           const display = raw === "" ? "(Blank)" : o.JobToName.trim();
//                           const key = display.toLowerCase();

//                           if (!acc[key]) acc[key] = display;
//                           return acc;
//                         }, {})
//                       ).map((job) => (
//                         <label key={job.toLowerCase()} className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={filterOptions[job.toLowerCase()] || false}
//                             onChange={(e) =>
//                               setFilterOptions((prev) => ({
//                                 ...prev,
//                                 [job.toLowerCase()]: e.target.checked,
//                               }))
//                             }
//                           />
//                           {job}
//                         </label>
//                       ))}
//                     </div>

//                     <div className="flex justify-end gap-3 mt-4">
//                       <Button
//                         onClick={() => setIsFilterOpen(false)}
//                         className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
//                       >
//                         Cancel
//                       </Button>
//                       <Button
//                         onClick={async () => {
//                           setIsFilterOpen(false);
//                           try {
//                             const data = await api.getSimplePOList(user.UsrName);
//                             let list = (data?.Table || []).map((o) => ({
//                               ...o,
//                               cpid: Number(o.cpid) || 0,
//                             }));

//                             // Unique orders
//                             list = list.filter(
//                               (obj, index, self) =>
//                                 index ===
//                                 self.findIndex(
//                                   (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
//                                 )
//                             );

//                             // Apply JobTo filter
//                             const selectedJobs = Object.keys(filterOptions).filter(
//                               (k) => filterOptions[k]
//                             );
//                             if (selectedJobs.length > 0) {
//                               list = list.filter((o) => {
//                                 const normalized = o.JobToName?.trim() || "";
//                                 const jobKey =
//                                   normalized === "" ? "(blank)" : normalized.toLowerCase();
//                                 return selectedJobs.includes(jobKey);
//                               });
//                             }

//                             setOrders(list);
//                             setFilteredOrders(list);
//                           } catch (err) {
//                             console.error("JobTo filter error:", err);
//                             alert("Failed to filter orders.");
//                           }
//                         }}
//                         className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
//                       >
//                         OK
//                       </Button>
//                     </div>
//                   </div>
//                 </div>
//               )}


//               {/* ✅ Buttons INSIDE modal */}
//               <div className="flex justify-end gap-3 mt-4">
//                 <Button
//                   onClick={() => setIsFilterOpen(false)}
//                   className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
//                 >
//                   Cancel
//                 </Button>
//                 <Button
//                   onClick={async () => {
//                     setIsFilterOpen(false);
//                     try {
//                       const data = await api.getSimplePOList(user.UsrName);
//                       let list = (data?.Table || []).map((o) => ({
//                         ...o,
//                         cpid: Number(o.cpid) || 0,
//                       }));

//                       list = list.filter(
//                         (obj, index, self) =>
//                           index ===
//                           self.findIndex(
//                             (t) => t.Poid === obj.Poid && t.InvNo === obj.InvNo
//                           )
//                       );

//                       // Apply JobTo filter (case-insensitive)
//                       const selectedJobs = Object.keys(filterOptions).filter(
//                         (k) => filterOptions[k]
//                       );
//                       if (selectedJobs.length > 0) {
//                         list = list.filter((o) =>
//                           selectedJobs.includes(o.JobToName?.toLowerCase().trim())
//                         );
//                       }

//                       setOrders(list);
//                       setFilteredOrders(list);
//                     } catch (err) {
//                       console.error("JobTo filter error:", err);
//                       alert("Failed to filter orders.");
//                     }
//                   }}
//                   className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
//                 >
//                   OK
//                 </Button>
//               </div>
//             </div>
//           </div>
//         )}


//       <Heading>Orders</Heading>

//       <div className="flex flex-col items-center gap-4 mb-6 md:flex-row md:justify-between">

//         <Button
//           className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
//                rounded-2xl shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//                transition-all duration-300 ease-in-out"
//           onClick={() => setView("add-order")}  >
//           <PlusCircle size={18} className="animate-pulse" />
//           <span className="font-medium">New</span>
//         </Button>


//         {/* 🔄 Toggle buttons */}
//         <div className="flex items-center justify-center p-1 bg-gray-100 rounded-lg">
//           <button
//             onClick={() => setViewMode("card")}
//             className={`p-2 rounded-md ${viewMode === "card"
//               ? "bg-white text-blue-600 shadow"
//               : "text-gray-500"
//               }`}
//           >
//             <GridIcon />
//           </button>
//           <button
//             onClick={() => setViewMode("table")}
//             className={`p-2 rounded-md ${viewMode === "table"
//               ? "bg-white text-blue-600 shadow"
//               : "text-gray-500"
//               }`}
//           >
//             <ListIcon />
//           </button>
//         </div>
//       </div>

//       {/* Search Bar */}
//       <div className="flex items-center gap-2 px-3 py-2 bg-gray border-b">
//         <Button
//           onClick={() => setView("home")}
//           className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
//         >
//           ⬅
//         </Button>
//         <SearchInput
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           placeholder="Search by vendor or mobile..."
//         />
//         <div className="relative">
//           {/* Dropdown Trigger */}
//           <Button
//             onClick={() => setIsDropdownOpen((prev) => !prev)}
//             className="flex items-center justify-center w-9 h-9 text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition"
//           >
//             <span className="text-lg">🔽</span> {/* cleaner icon than 🔽 */}
//           </Button>

//           {/* Dropdown Menu */}
//           {isDropdownOpen && (
//             <div
//               className={`absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 transition-all duration-200 ease-out ${isDropdownOpen
//                   ? "opacity-100 scale-100"
//                   : "opacity-0 scale-95 pointer-events-none"
//                 }`}
//             >
//               <button
//                 className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-xl"
//                 onClick={() => {
//                   setActiveFilterType("invoice");
//                   setIsDropdownOpen(false);
//                   setIsFilterOpen(true);
//                 }}
//               >
//                 <span className="text-base">📑</span>
//                 <span>Filter by Invoice</span>
//               </button>

//               <button
//                 className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
//                 onClick={() => {
//                   setActiveFilterType("packing");
//                   setIsDropdownOpen(false);
//                   setIsFilterOpen(true);
//                 }}
//               >
//                 <span className="text-base">📦</span>
//                 <span>Filter by Packing</span>
//               </button>

//               <button
//                 className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-xl"
//                 onClick={() => {
//                   setActiveFilterType("jobto");
//                   setIsDropdownOpen(false);
//                   setIsFilterOpen(true);
//                 }}
//               >
//                 <span className="text-base">🛠</span>
//                 <span>Filter by JobTo</span>
//               </button>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* List / Table Content */}
//       {
//         loading ? (
//           <Spinner />
//         ) : error ? (
//           <MessageBox message={error} />
//         ) : filteredOrders.length === 0 ? (
//           <div className="py-12 text-center text-gray-500">No orders found.</div>
//         ) : viewMode === "card" ? (
//           // ✅ Card View
//           <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
//             {filteredOrders.map((order) => (
//               <OrderCard
//                 key={order.Poid}
//                 order={order}
//                 onEdit={() => {
//                   setEditingContext({
//                     id: order.Poid,
//                     customerId: order.Vid,
//                     orderDate: order.Podate,
//                   });
//                   setView("edit-order");
//                 }}
//                 MakeReceipt={() => handleMakeReceipt(order)}
//                 MakeInvoice={() => handleMakeInvoice(order)}
//                 onPrintInvoice={() => handlePrintInvoice(order)}
//                 onPackingSlip={() => handlePackingSlip(order)}
//                 receiptDisabled={disabledReceipts.has(order.Poid)}
//                 invoiceDisabled={
//                   disabledInvoices.has(order.Poid) || Number(order.cpid) > 0
//                 }
//                 orderStatuses={orderStatuses}        // ✅ now available in OrderCard
//                 handlePending={handlePending}        // ✅ passed down
//                 handleJobto={handleJobto}            // ✅ passed down
//               />
//             ))}
//           </div>
//         ) : (
//           // ✅ Table View
//           <div className="overflow-x-auto bg-white border border-gray-200 shadow-md rounded-xl mt-4">
//             <table className="w-full text-sm text-left">
//               <thead className="tracking-wider text-gray-900 uppercase bg-gray-100">
//                 <tr>
//                   <th className="p-3">PO ID</th>
//                   <th className="p-3">Vendor</th>
//                   <th className="p-3">Date</th>
//                   <th className="p-3">Invoice</th>
//                   <th className="p-3">Total</th>
//                   <th className="p-3 text-center">Actions</th>
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-gray-200">
//                 {filteredOrders.map((order) => (
//                   <tr key={order.Poid} className="hover:bg-gray-50">
//                     <td className="p-3">#{order.Poid}</td>
//                     <td className="p-3">{order.Vname}</td>
//                     <td className="p-3">
//                       {order.DeliveryDate
//                         ? new Date(order.DeliveryDate).toLocaleDateString()
//                         : ""}
//                     </td>
//                     <td className="p-3">{order.InvNo || "-"}</td>
//                     <td className="p-3">{order.OrderTotal}</td>
//                     <td className="p-3 text-center">
//                       <div className="flex justify-center gap-2 flex-wrap">
//                         <Button
//                           className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-1 text-xs font-medium text-white 
//              bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
//              hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//              transition-all duration-300 ease-in-out"
//                           onClick={() => {
//                             setEditingContext({
//                               id: order.Poid,
//                               customerId: order.Vid,
//                               orderDate: order.Podate,
//                             });
//                             setView("edit-order");
//                           }}
//                         >
//                           <FileEdit className="w-6 h-6" />
//                           Edit
//                         </Button>

//                         <Button
//                           className={`min-w-[70px] px-2 py-1 rounded ${disabledReceipts.has(order.Poid)
//                             ? "bg-gray-300 text-gray-600"
//                             : "bg-blue-500 text-white hover:bg-blue-600"
//                             }`}
//                           onClick={() => handleMakeReceipt(order)}
//                           disabled={disabledReceipts.has(order.Poid)}
//                         >
//                           Receipt
//                         </Button>

//                         <Button
//                           className="min-w-[70px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
//                           onClick={() => handlePrintInvoice(order)}
//                         >
//                           Invoice
//                         </Button>

//                         <Button
//                           className="min-w-[70px] px-2 py-1 text-white bg-green-500 rounded hover:bg-green-600"
//                           onClick={() => handlePackingSlip(order)}
//                         >
//                           Packing
//                         </Button>
//                       </div>
//                     </td>

//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )
//       }
//     </div >
//   );
// };

// export default OrderListPage;


